import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  chartId?: string;
  fallbackContent?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  hideOnError?: boolean;
}

interface State {
  hasError: boolean;
  shouldHide: boolean;
}

class ChartErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, shouldHide: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI or hide the component
    return { hasError: true, shouldHide: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error for debugging purposes
    console.warn(`Chart rendering failed${this.props.chartId ? ` for chart ${this.props.chartId}` : ''}, hiding component:`, error);
    
    // Call the optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      // If hideOnError is true (default), render nothing to completely hide the chart
      if (this.props.hideOnError !== false) {
        return null;
      }
      
      // Otherwise, show fallback content if provided
      if (this.props.fallbackContent) {
        return this.props.fallbackContent;
      }
      
      // Default fallback (should rarely be seen due to hideOnError default)
      return (
        <div style={{ 
          padding: '10px', 
          color: '#666', 
          fontStyle: 'italic',
          textAlign: 'center'
        }}>
          Chart unavailable on this device
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChartErrorBoundary; 